/**
 * Wire-level shapes returned by the QAP backend, transcribed verbatim from
 * `app/api/query.py` and `app/api/health.py`.
 *
 * Nothing outside `lib/api` should import from this file. The rest of the app
 * consumes the normalised domain models in `./models.ts`; the mappers in
 * `./mappers.ts` are the single place that knows about PascalCase keys, the
 * `Data` envelope, or the two different error shapes the API can produce.
 */

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

export interface QueryRequestDto {
  query: string
  conversation_id: string
  database?: string
}

// ---------------------------------------------------------------------------
// Query responses
// ---------------------------------------------------------------------------

/** A single cell. SQL can hand back anything JSON-representable. */
export type CellDto = string | number | boolean | null

export interface QueryDataDto {
  Columns: string[]
  Rows: CellDto[][]
  RowCount: number
  DisplayedRowCount: number
  Truncated: boolean
  /** Only present when the backend truncated the result set. */
  Note?: string
}

export interface QueryMetaDto {
  HasResults: boolean
  UsedLLM: boolean
  Truncated?: boolean
  IsConversational: boolean
  /** `/query/test` only. "DETERMINISTIC" | "INVESTIGATIVE" | ... */
  AnalysisMode?: string
  /** `/query/test` only. Number of follow-up investigation queries run. */
  Investigations?: number
}

export interface QuerySuccessDto {
  Answer: string
  Data: QueryDataDto
  ConversationId: string | null
  Meta: QueryMetaDto
  Status?: 'ok' | 'needs_clarification' | 'failed_generic'
  Message?: string
  Suggestions?: string[]
}

/**
 * `/query/test` swallows unexpected exceptions and returns this with HTTP 200.
 * A client that only checks `res.ok` will silently render nothing, so the
 * mapper detects it explicitly.
 */
export interface QueryTestFailureDto {
  status: 'error'
  error: string
  query: string
  conversation_id: string
}

export type QueryResponseDto = QuerySuccessDto | QueryTestFailureDto

export function isQueryTestFailure(
  body: unknown,
): body is QueryTestFailureDto {
  return (
    typeof body === 'object' &&
    body !== null &&
    (body as { status?: unknown }).status === 'error'
  )
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/**
 * `register_exception_handlers` in `app/core/exceptions.py` wraps every
 * HTTPException as `{ success: false, error: <detail> }`. `detail` is usually a
 * string, but the 503 raised for a missing LLM config is a structured object.
 */
export interface ApiErrorEnvelopeDto {
  success: false
  error: string | { code?: string; message?: string } | unknown
}

// ---------------------------------------------------------------------------
// Health
// ---------------------------------------------------------------------------

export interface HealthDto {
  status: string
  application: string
  version: string
  environment: string
}

export interface LivenessDto {
  status: string
}

export interface RootDto {
  application: string
  version: string
  environment: string
  status: string
}
